import { type SchemaDefinition, TemplatePropertyName } from '@sap/ux-specification-types';
import { v4 } from '@sap/ux-specification-types';
import { getDefinitionKey } from '../../../common/utils';
import { addDescriptiveEnumDefinition } from '../../../common/generate/utils';
import type { XMLElement } from '@xml-tools/ast';
import { getAttribute } from './utils';
import type { CustomExtension, CustomExtensions } from '../../utils/utils';
import { addExtensionToArray } from '../../utils/utils';

const ANNOTATION_PREFIX_MAP_REPLACE = new Map([['filterFields', 'SelectionField::']]);

const ANNOTATION_PREFIX_MAP = new Map([
    ['columns', 'DataField::'],
    ['actions', 'DataFieldForAction::'],
    ['fields', 'DataField::']
]);

const PLACEMENT_PROPERTIES = {
    anchor: 'anchor',
    placement: 'placement',
    key: 'key'
};

interface ExtensionData {
    key: string;
    extension: CustomExtension;
}

interface AnnotationPrefix {
    prefix: string;
    replace: boolean;
}

/**
 * Method checks if passed schema definition is custom extension.
 * Handles both direct class references and interface references (which are resolved to their implementations).
 *
 * @param {SchemaDefinition} schema Schema object which contains custom extensions and annotation elements.
 * @param {SchemaDefinition} appSchema Full app schema.
 * @returns {boolean} True if the schema supports custom extensions with anchor/placement properties.
 */
function isCustomExtensionSchema(schema: SchemaDefinition, appSchema: SchemaDefinition): boolean {
    if (typeof schema?.additionalProperties === 'object' && schema?.additionalProperties.$ref) {
        const definition = appSchema.definitions?.[getDefinitionKey(schema.additionalProperties.$ref)];
        const additionalProperties = typeof definition === 'object' ? definition.properties || {} : {};
        return (
            PLACEMENT_PROPERTIES.anchor in additionalProperties &&
            PLACEMENT_PROPERTIES.placement in additionalProperties
        );
    }
    return false;
}

/**
 * Method returns annotation prefix for passed macro node name.
 *
 * @param {string} nodeName Macros node name.
 * @returns {AnnotationPrefix | undefined} The annotation prefix object or undefined if no prefix is found.
 */
function getAnnotationPrefix(nodeName: string): AnnotationPrefix | undefined {
    let prefix = ANNOTATION_PREFIX_MAP_REPLACE.get(nodeName);
    const result: AnnotationPrefix = { prefix: '', replace: false };
    if (prefix) {
        result.prefix = prefix;
        result.replace = true;
    } else {
        prefix = ANNOTATION_PREFIX_MAP.get(nodeName);
        if (prefix) {
            result.prefix = prefix;
        }
    }
    return result;
}

/**
 * Method returns proxy extension object by parsing XML node.
 * Proxy object is required to be able to reuse 'utils.addExtensionToArray' method to recalculate order of annotation and custom extensions.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object which contains custom extensions and annotation elements.
 * @param {string} property Custom extension key in schema's properties.
 * @param {string} aggregationName Aggregation name of container.
 * @returns {ExtensionData} Proxy extension object.
 */
function getExtensionData(
    node: XMLElement,
    schema: SchemaDefinition,
    property: string,
    aggregationName: string
): ExtensionData {
    const schemaNode = schema.properties[property] as SchemaDefinition;
    const annotationPrefix = getAnnotationPrefix(aggregationName);
    // Custom extension node
    const xmlPath = schemaNode.metadata.path;
    const xmlExtension = node.subElements[xmlPath[xmlPath.length - 1].index];
    const key = getAttribute(xmlExtension, PLACEMENT_PROPERTIES.key) || property;
    const anchor = getAttribute(xmlExtension, PLACEMENT_PROPERTIES.anchor);
    const placement = getAttribute(xmlExtension, PLACEMENT_PROPERTIES.placement) || v4.Placement.After;
    const annotationAnchor = annotationPrefix.replace ? `${annotationPrefix.prefix}${anchor}` : anchor;
    const extension = {
        position: {
            anchor: annotationAnchor in schema.properties ? annotationAnchor : anchor,
            placement
        }
    } as CustomExtension;
    return { key, extension };
}

/**
 * Method updates 'propertyIndex' for passed schema's properties.
 * Method prepares proxy objects and reuses 'utils.addExtensionToArray' method to recalculate order of annotation and custom extensions.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object which contains custom extensions and annotation elements.
 * @param {string} aggregationName Aggregation name of container.
 */
function updatePropertyIndices(node: XMLElement, schema: SchemaDefinition, aggregationName: string): void {
    // Reuse "addExtensionToArray" method for calculating extensions position as it is done for manifest based extensions
    // Logic requires to create 2 proxy objects to be able to reuse same logic
    // Custom extension entries
    const extensions: CustomExtensions = {};
    // Annotation entries
    const sortedExtensionsArray: [string, SchemaDefinition][] = [];
    // Custom extension mapping between schema property key and extension key in xml
    const extensionSchemaKeyMap: { [key: string]: string } = {};
    // Populate annotation and custom extension entries
    for (const property in schema.properties) {
        const schemaNode = schema.properties[property] as SchemaDefinition;
        if ('metadata' in schemaNode && schemaNode.metadata?.path !== undefined) {
            // Custom extension node (has a path to an XML element)
            const { extension, key } = getExtensionData(node, schema, property, aggregationName);
            extensions[key] = extension;
            // Mapping between extension key in xml and key in schema
            extensionSchemaKeyMap[key] = property;
        } else {
            // Annotation based node
            sortedExtensionsArray.push([property, schemaNode]);
        }
    }
    // Reuse method to calculate order
    addExtensionToArray(extensions, sortedExtensionsArray, '', TemplatePropertyName.Position);
    // Apply order to 'propertyIndex' properties in schema
    for (let i = 0; i < sortedExtensionsArray.length; i++) {
        const key = sortedExtensionsArray[i][0];
        let schemaNode = schema.properties[key] as SchemaDefinition;
        if (sortedExtensionsArray[i][1].$ref === '#/definitions/' && key in extensionSchemaKeyMap) {
            // Entry of custom extension
            schemaNode = schema.properties[extensionSchemaKeyMap[key]] as SchemaDefinition;
        }

        if (schemaNode) {
            // Update index to affect ordering
            schemaNode.propertyIndex = i;
        }
    }
}

/**
 * Method applies correction to anchor enum entry based on collection element and anchor type(annotation or custom extension).
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {string} originalKey Original full enum key
 * @param {AnnotationPrefix} annotationPrefix Annotation prefix data - data depends on collection element.
 * @param {SchemaDefinition} property Reference/anchored element from schema.
 * @returns {string} Corrected anchor enum key.
 */
function getCorrectedAnchorEnumKey(
    node: XMLElement,
    originalKey: string,
    annotationPrefix: AnnotationPrefix,
    property?: SchemaDefinition
): string {
    let key = originalKey;
    if (annotationPrefix.prefix && key.startsWith(annotationPrefix.prefix)) {
        if (annotationPrefix.replace) {
            // Remove annotation prefix - it is not required for some macros elements(currently FilterField)
            key = key.replace(annotationPrefix.prefix, '');
        }
    } else if (property?.metadata?.path) {
        // Reference to custom extension - use key for enum value
        const index = property.metadata.path[property.metadata.path.length - 1].index;
        key = getAttribute(node.subElements[index], 'key') || key;
    }
    return key;
}

/**
 * Method refreshes custom extension schema's 'anchor' property with 'oneOf' enum entires.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object which contains custom extensions and annotation elements.
 * @param {SchemaDefinition} appSchema Full app schema.
 * @param {string} aggregationName Aggregation name of container.
 */
function applyEnumsForAnchors(
    node: XMLElement,
    schema: SchemaDefinition,
    appSchema: SchemaDefinition,
    aggregationName: string
): void {
    const annotationPrefix = getAnnotationPrefix(aggregationName);
    for (const name in schema.properties) {
        const anchorProperty = 'anchor';
        const property = schema.properties[name] as SchemaDefinition;
        if (!property.metadata) {
            continue;
        }
        addDescriptiveEnumDefinition(
            appSchema,
            schema,
            {
                property: anchorProperty,
                schema: property
            },
            {
                excludeKey: name,
                resolveDefinition: true
            }
        );
        // Remove annotation prefix from anchor enum entries - it is difference between manifest and macros anchoring
        const oneOf = (property.properties?.[anchorProperty] as SchemaDefinition)?.oneOf || [];
        for (const entry of oneOf) {
            if (typeof entry === 'object' && typeof entry.const === 'string') {
                entry.const = getCorrectedAnchorEnumKey(
                    node,
                    entry.const,
                    annotationPrefix,
                    schema.properties[entry.const] as SchemaDefinition
                );
            }
        }
    }
}

/**
 * Method handles custom extension - resolve positions and ordering.
 * Currently it handles `sap.fe.macros.FilterField`, but code is generic and can handle also other extensions - method relies on 'anchor' and 'placement' properties.
 *
 * @param node Parsed XML element.
 * @param schema Schema object which contains custom extensions and annotation elements.
 * @param appSchema Full app schema.
 * @param aggregationName Name of the aggregation container.
 */
export function handleExtensions(
    node: XMLElement,
    schema: SchemaDefinition,
    appSchema: SchemaDefinition,
    aggregationName?: string
): void {
    if (!isCustomExtensionSchema(schema, appSchema)) {
        // Schema does not support custom extensions
        return;
    }
    aggregationName = aggregationName || node.name;

    // Update ordering of nodes by updating 'propertyIndex' for each node
    updatePropertyIndices(node, schema, aggregationName);
    // Enhance anchor property to add enum
    applyEnumsForAnchors(node, schema, appSchema, aggregationName);
}
